import { motion } from "framer-motion";
import ReactPhoneInput from "react-phone-number-input";
import { useT } from "@/lib/i18n";
import { getQuizEventTypeContext } from "@/lib/quizEventType";
import { getDefaultPhoneCountry } from "@/lib/phoneCountry";
import ScreenHeading from "./ScreenHeading";
import { HEADING_GAP, LIST_GAP, ROW_BORDER, ROW_SHAPE, ROW_SURFACE } from "../layout";
import type { ContactScreenDef, ScreenComponentProps } from "../types";

/**
 * Phone and Instagram, asked after the welcome rather than before the account.
 *
 * In the control flow this sits ahead of the auth gate and costs a third of
 * everyone who reaches it: a phone number is the most personal thing the
 * signup asks for, and it is asked before the reader has anything to show for
 * it. Here the account already exists and the welcome has already landed, so
 * the question reads as "how do we reach you about your table" rather than as
 * one more toll on the way in.
 *
 * Both fields are optional at this position. Nothing downstream is blocked by
 * a missing number — the table is booked, the group chat is in the app — so
 * holding someone here would cost more than the field is worth.
 *
 * Copy is the control stepper's (`quiz.stepper.contact.*`), already translated
 * in all four locales: the two surfaces ask exactly the same thing, and one
 * string means they cannot drift.
 */
export default function ContactScreen({
  def,
  rt,
}: ScreenComponentProps<ContactScreenDef>) {
  const { t, language } = useT();
  // "dinner" / "coffee" so the reason given matches what they were sold.
  const eventTypeContext = getQuizEventTypeContext();
  const defaultCountry = getDefaultPhoneCountry(language);

  const phone = (rt.answers.phone as string | undefined) ?? "";
  const instagram = (rt.answers.instagram as string | undefined) ?? "";

  return (
    <div className="flex-1 flex flex-col">
      <ScreenHeading
        title={t(`${def.copyKey}.headline`)}
        subtitle={t(`${def.copyKey}.subtitle`, { context: eventTypeContext })}
      />

      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className={`${HEADING_GAP} flex flex-col ${LIST_GAP}`}
      >
        {/* Both fields wear the flow's own row chrome rather than the app's
            `input-glass`, so they read as the same control stack as the city
            search and the option rows. ReactPhoneInput's descendants are made
            transparent so the wrapper alone draws the frame. */}
        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="onboarding-phone"
            className="text-[13px] font-medium text-neutral-900 dark:text-neutral-50"
          >
            {t(`${def.copyKey}.phoneLabel`)}
          </label>
          <div
            className={`${ROW_BORDER} ${ROW_SHAPE} ${ROW_SURFACE} focus-within:border-primary-500 transition-colors [&_.PhoneInputInput]:bg-transparent [&_.PhoneInputInput]:border-0 [&_.PhoneInputInput]:outline-none [&_.PhoneInputInput]:text-[15px] [&_.PhoneInputInput]:text-neutral-900 dark:[&_.PhoneInputInput]:text-neutral-50 [&_.PhoneInputInput]:placeholder-neutral-400 [&_.PhoneInputCountrySelect]:text-neutral-900 dark:[&_.PhoneInputCountrySelect]:text-neutral-50 [&_.PhoneInputCountrySelectArrow]:text-neutral-500`}
          >
            <ReactPhoneInput
              id="onboarding-phone"
              defaultCountry={defaultCountry as never}
              placeholder={t("quiz.questions.phone.placeholder")}
              value={phone}
              onChange={(v) => rt.setAnswer("phone", v?.toString() ?? "")}
            />
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="onboarding-instagram"
            className="text-[13px] font-medium text-neutral-900 dark:text-neutral-50"
          >
            {t(`${def.copyKey}.instagramLabel`)}{" "}
            <span className="font-normal text-neutral-500 dark:text-neutral-400">
              {t(`${def.copyKey}.instagramOptional`)}
            </span>
          </label>
          <div
            className={`${ROW_BORDER} ${ROW_SHAPE} ${ROW_SURFACE} focus-within:border-primary-500 transition-colors`}
          >
            <input
              id="onboarding-instagram"
              type="text"
              inputMode="text"
              autoComplete="off"
              value={instagram}
              onChange={(e) => rt.setAnswer("instagram", e.target.value)}
              placeholder={t(`${def.copyKey}.instagramPlaceholder`)}
              className="w-full bg-transparent border-0 p-0 text-[15px] leading-snug text-neutral-900 dark:text-neutral-50 placeholder:text-neutral-400 focus:outline-none focus:ring-0"
            />
          </div>
        </div>
      </motion.div>

      <p className="mt-5 text-[11px] leading-relaxed text-neutral-500 dark:text-neutral-400">
        {t(`${def.copyKey}.footer`, { context: eventTypeContext })}
      </p>
    </div>
  );
}
